<template>
  <div id="lucid-pricing-view">
    <v-progress-linear v-if="loading" :indeterminate="true" class="ma-0" color="accent" :height="5"></v-progress-linear>
    <div v-if="!loading" id="view-sub-nav" class="justify-end">
      <v-btn class="ml-2" variant="outlined" color="primary" @click="csv">Download CSV</v-btn>
    </div>
    <v-container fluid v-if="!loading">
      <v-row>
        <v-col cols="12">
          <h2>Materials</h2>
        </v-col>

        <v-col v-for="material in pricedMaterials" class="elevation-0" :key="material.id" cols="12">
          <v-row>
            <v-col cols="6" class="py-1"
              ><span>{{ material.thickness }}mm - </span>{{ material.label }} ({{ sheetCount[material.id] }})
              <span v-if="material.handlesCost == 0 && material.size">[{{ material.size }}]</span>
            </v-col>
            <v-col cols="6" class="py-1 text-right"
              >${{ $numeral(combinedCosts[material.id].materialTotalCost).format('0,0.00') }}</v-col
            >
          </v-row>
        </v-col>
        <v-col v-for="copyMaterial in copyMaterialData" class="elevation-0" :key="copyMaterial.id" cols="12">
          <v-row>
            <v-col cols="6" class="py-1"
              >{{ copyMaterial.label }} ({{ copyMaterial.sheetCount }}) [{{ copyMaterial.sheetSize }}]</v-col
            >
            <v-col cols="6" class="py-1 text-right"
              >${{ $numeral(copyMaterial.materialTotalCost).format('0,0.00') }}</v-col
            >
          </v-row>
        </v-col>
        <v-col>
          <v-divider class="mt-1 mb-4" />
          <v-row>
            <v-col cols="6" class="py-1"><h4>Material Total</h4></v-col>
            <v-col cols="6" class="py-1 text-right"
              ><strong>${{ $numeral(combinedCosts.material).format('0,0.00') }}</strong></v-col
            >
          </v-row>
          <v-divider class="mt-4" />
        </v-col>
      </v-row>
      <v-row class="mt-6">
        <v-col cols="12">
          <h2>Breakdown</h2>
        </v-col>
        <v-col cols="12">
          <v-row>
            <v-col cols="6" class="py-1">Total Area Sq Ft</v-col>
            <v-col cols="6" class="py-1 text-right">{{ $utils.toFixedNumber(combinedCosts.area, 2) }} sq ft</v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="py-1">Total Linear Ft</v-col>
            <v-col cols="6" class="py-1 text-right">{{ $utils.toFixedNumber(combinedCosts.perimeter, 2) }} ft</v-col>
          </v-row>
          <v-row v-if="primaryCosts.jHandlesPerimeter > 0">
            <v-col cols="6" class="py-1">J-Handles Linear Ft</v-col>
            <v-col cols="6" class="py-1 text-right"
              >{{ $utils.toFixedNumber(primaryCosts.jHandlesPerimeter, 2) }} ft</v-col
            >
          </v-row>
        </v-col>
      </v-row>
      <v-row class="mt-6">
        <v-col cols="12">
          <h2>Primary Costs</h2>
        </v-col>
        <v-col cols="12">
          <!-- Primary area/linear (show only if urtils exist) -->
          <v-row v-if="hasUrtils">
            <v-col cols="6" class="py-1">Primary Area Sq Ft</v-col>
            <v-col cols="6" class="py-1 text-right">{{ $utils.toFixedNumber(primaryCosts.area, 2) }} sq ft</v-col>
          </v-row>
          <v-row v-if="hasUrtils">
            <v-col cols="6" class="py-1">Primary Linear Ft</v-col>
            <v-col cols="6" class="py-1 text-right">{{ $utils.toFixedNumber(primaryCosts.perimeter, 2) }} ft</v-col>
          </v-row>
          <v-divider v-if="hasUrtils" class="mt-2 mb-2" />
          <v-row v-if="version.pricing?.worktops?.cost > 0">
            <v-col cols="6" class="py-1">Worktops</v-col>
            <v-col cols="6" class="py-1 text-right"
              >${{ $numeral(version.pricing.worktops.cost).format('0,0.00') }}</v-col
            >
          </v-row>
          <v-row>
            <v-col cols="6" class="py-1">CAD</v-col>
            <v-col cols="6" class="py-1 text-right">${{ $numeral(primaryCosts.cad).format('0,0.00') }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="py-1">CNC</v-col>
            <v-col cols="6" class="py-1 text-right">${{ $numeral(primaryCosts.cutting).format('0,0.00') }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="py-1">Edge Finishing</v-col>
            <v-col cols="6" class="py-1 text-right">${{ $numeral(primaryCosts.edgeFinishing).format('0,0.00') }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="py-1">Face Finishing</v-col>
            <v-col cols="6" class="py-1 text-right">${{ $numeral(primaryCosts.faceFinishing).format('0,0.00') }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="py-1">Handles ({{ combinedCosts.numHandles }})</v-col>
            <v-col cols="6" class="py-1 text-right">${{ $numeral(primaryCosts.handlesCost).format('0,0.00') }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="py-1">Pack & Crate</v-col>
            <v-col cols="6" class="py-1 text-right">${{ $numeral(primaryCosts.packaging).format('0,0.00') }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="py-1">Pack & Crate Per Sq Foot</v-col>
            <v-col cols="6" class="py-1 text-right"
              >${{ $numeral(primaryCosts.packagingPerSqFt).format('0,0.00') }}</v-col
            >
          </v-row>
        </v-col>
        <v-col cols="12">
          <v-row>
            <v-col cols="6" class="py-1">Freight</v-col>
            <v-col cols="6" class="py-1 text-right"
              >${{ $numeral((version.pricing?.shipping || 0) * 2).format('0,0.00') }}</v-col
            >
          </v-row>
        </v-col>
        <v-col cols="12">
          <v-divider class="mt-1 mb-4" />
          <v-row>
            <v-col cols="6" class="py-1"><strong>Total Primary Costs</strong></v-col>
            <v-col cols="6" class="py-1 text-right"
              ><strong>${{ $numeral(totalPrimaryCosts).format('0,0.00') }}</strong></v-col
            >
          </v-row>
          <v-divider class="mt-4" />
        </v-col>
      </v-row>
      <!-- URTILS COSTS -->
      <v-row class="mt-6" v-if="hasUrtils">
        <v-col cols="12">
          <h2>Urtils Costs</h2>
        </v-col>
        <v-col cols="12">
          <v-row>
            <v-col cols="6" class="py-1">Urtil Area Sq Ft</v-col>
            <v-col cols="6" class="py-1 text-right"
              >{{ $utils.toFixedNumber(version.pricing.urtils.area, 2) }} sq ft</v-col
            >
          </v-row>
          <v-row>
            <v-col cols="6" class="py-1">Urtil Linear Ft</v-col>
            <v-col cols="6" class="py-1 text-right"
              >{{ $utils.toFixedNumber(version.pricing.urtils.perimeter, 2) }} ft</v-col
            >
          </v-row>
          <v-divider class="mt-2 mb-2" />
          <v-row>
            <v-col cols="6" class="py-1">CAD</v-col>
            <v-col cols="6" class="py-1 text-right"
              >${{ $numeral(version.pricing.urtils.programmingCost).format('0,0.00') }}</v-col
            >
          </v-row>
          <v-row>
            <v-col cols="6" class="py-1">CNC</v-col>
            <v-col cols="6" class="py-1 text-right"
              >${{ $numeral(version.pricing.urtils.cuttingCost).format('0,0.00') }}</v-col
            >
          </v-row>
          <v-row>
            <v-col cols="6" class="py-1">Edge Finishing</v-col>
            <v-col cols="6" class="py-1 text-right"
              >${{ $numeral(version.pricing.urtils.edgeFinishingCost).format('0,0.00') }}</v-col
            >
          </v-row>
          <v-row>
            <v-col cols="6" class="py-1">Face Finishing</v-col>
            <v-col cols="6" class="py-1 text-right"
              >${{ $numeral(version.pricing.urtils.surfaceFinishingCost).format('0,0.00') }}</v-col
            >
          </v-row>
          <v-row v-if="version.pricing.urtils.glassDoorCost > 0">
            <v-col cols="6" class="py-1">Glass Doors</v-col>
            <v-col cols="6" class="py-1 text-right"
              >${{ $numeral(version.pricing.urtils.glassDoorCost).format('0,0.00') }}</v-col
            >
          </v-row>
          <v-row>
            <v-col cols="6" class="py-1">Packaging Fixed Cost</v-col>
            <v-col cols="6" class="py-1 text-right"
              >${{ $numeral(version.pricing.urtils.packaging).format('0,0.00') }}</v-col
            >
          </v-row>
          <v-row>
            <v-col cols="6" class="py-1">Pack & Crate Per Sq Ft</v-col>
            <v-col cols="6" class="py-1 text-right"
              >${{ $numeral(version.pricing.urtils.packagingPerSqFt).format('0,0.00') }}</v-col
            >
          </v-row>
        </v-col>
        <v-col cols="12">
          <v-divider class="mt-1 mb-4" />
          <v-row>
            <v-col cols="6" class="py-1"><strong>Total Urtils Costs</strong></v-col>
            <v-col cols="6" class="py-1 text-right"
              ><strong>${{ $numeral(version.pricing.urtils.cost).format('0,0.00') }}</strong></v-col
            >
          </v-row>
          <v-divider class="mt-4" />
        </v-col>
      </v-row>
      <v-row class="mt-10">
        <v-col cols="6" :class="version.lucidCoverCost ? 'text-red' : ''">
          <h2>Lucid Total</h2>
        </v-col>
        <v-col cols="6" :class="version.lucidCoverCost ? 'text-right text-red' : 'text-right'">
          <h2>${{ $numeral(total).format('0,0.00') }}</h2>
          <h4 v-if="version.lucidCoverCost">(Recut Covered by Lucid)</h4>
        </v-col>
      </v-row>
      <v-divider class="mt-1 mb-4" />
      <v-row v-if="$store.state.user.permissions.admin">
        <v-col cols="6" class="py-1"><h4>Customer Total Payment</h4></v-col>
        <v-col cols="6" class="py-1 text-right"
          ><strong>${{ $numeral(customerPaidTotal).format('0,0.00') }}</strong></v-col
        >
      </v-row>
      <v-divider v-if="$store.state.user.permissions.admin" class="mt-4" />
    </v-container>
  </div>
</template>

<script>
import productData from '@/js/productData'
import usPricing from '@/js/pricing/usPricing'
import MasterPricing from '@/js/pricing/MasterPricing'
import * as MaterialResolver from '@/js/MaterialResolver'
import utils from '@/js/utils'
import store from '@/store'
import { db } from '@/js/db'
import { doc, getDoc } from 'firebase/firestore'
import filesaver from 'file-saver'

export default {
  name: 'LucidPricingView',
  props: ['orderID'],
  data() {
    return {
      productData: productData,
      version: {},
      order: {},
      materials: [],
      loading: true
    }
  },
  watch: {
    orderID() {
      this.loadPricing()
    }
  },
  mounted() {
    this.loadPricing()
  },
  computed: {
    // Replaces `v-if` on the same element as `v-for`, which throws in Vue 3.
    pricedMaterials() {
      return this.materials.filter(
        (material) => material.itemCount > 0 && material.thickness !== 4 && material.value !== 'clearGlass'
      )
    },
    hasUrtils() {
      return this.version?.pricing?.urtils?.cost > 0
    },
    customerPaidTotal() {
      let paidTotal = 0
      this.order.payments.forEach((payment) => {
        if (payment.amount > 0) {
          paidTotal += payment.amount
        }
      })
      return paidTotal
    },
    sheetCount() {
      let sheetCount = {}
      for (const m of this.materials) {
        if (this.version.materialUsage[m.id]?.qtyOverride > 0) {
          sheetCount[m.id] = Math.ceil(this.version.materialUsage[m.id].qtyOverride)
        } else {
          sheetCount[m.id] = m.requiredSheetsRounded
        }
      }
      return sheetCount
    },
    totalSheetCount() {
      let totalSheetCount = 0
      for (const m of this.materials) {
        if (this.version.materialUsage[m.id]?.qtyOverride > 0) {
          totalSheetCount += Math.ceil(this.version.materialUsage[m.id].qtyOverride)
        } else {
          totalSheetCount += m.requiredSheetsRounded
        }
      }

      if (this.version.copyMaterial && this.version.copyMaterial.length > 0) {
        for (const copy of this.version.copyMaterial) {
          totalSheetCount += copy.qtyOverride ? copy.qtyOverride : 1
        }
      }
      return totalSheetCount
    },
    copyMaterialData() {
      let copyMaterialArray = []
      if (this.version.copyMaterial && this.version.copyMaterial.length > 0) {
        let copyMaterial = {
          id: '',
          label: '',
          sheetCount: 0,
          sheetSize: '',
          materialTotalCost: 0
        }

        for (const copy of this.version.copyMaterial) {
          copyMaterial.id = copy.id
          copyMaterial.label = copy.name
          copyMaterial.sheetCount = copy.qtyOverride ? copy.qtyOverride : 1
          copyMaterial.sheetSize = `${copy.sheetOverride.w}x${copy.sheetOverride.h}`
          copyMaterial.materialTotalCost = copyMaterial.sheetCount * copy.sheetOverride.cost
          copyMaterialArray.push(copyMaterial)
        }
      }
      return copyMaterialArray
    },
    primaryCosts() {
      const mmPerFoot = 304.8
      const sqMmPerSqFoot = 92903.04

      let costs = {
        material: 0,
        cad: 0,
        cutting: 0,
        packaging: 0,
        packagingPerSqFt: 0,
        edgeFinishing: 0,
        faceFinishing: 0,
        handlesCost: 0,
        jHandlesPerimeter: 0,
        numHandles: 0,
        perimeter: 0,
        area: 0
      }
      costs.packaging = utils.toFixedNumber(store.state.pricing.usCosts.packAndCrateFixedCost.value, 2)

      for (const m of this.materials) {
        // Skip clearGlass — priced via glass door formula, not sheet costs
        if (m.value === 'clearGlass') continue
        // Check if material has usage (total > 0 or sheets required)
        if ((m.total > 0 || m.requiredSheetsRounded > 0) && this.sheetCount[m.id]) {
          costs[m.id] = {}
          costs[m.id].materialTotalCost = this.sheetCount[m.id] * (m.sheetCost || 0)
          costs.material += costs[m.id].materialTotalCost
          if (m.sheetCost > 0) {
            // 4mm sheet is not included in price (=$0)
            costs.cad += m.programmingCost || 0
            costs.cutting += m.cuttingCost || 0
            costs.edgeFinishing += m.edgeFinishingCost || 0
            costs.faceFinishing += m.surfaceFinishingCost || 0
            costs.packagingPerSqFt += m.packagingPerSqFt || 0
            costs.perimeter += (m.perimeter || 0) / mmPerFoot
            costs.area += (m.area || 0) / sqMmPerSqFoot
          }
          if (m.lucidHandlesCost > 0) {
            // costs.faceFinishing += m.surfaceFinishingC
            costs.jHandlesPerimeter += m.jHandlesPerimeterFt || 0
            costs.numHandles += m.numHandles || 0
            costs.handlesCost += m.lucidHandlesCost
          }
        }
      }

      // add on copyMaterial sheet costs
      if (this.version.copyMaterial && this.version.copyMaterial.length > 0) {
        for (const copy of this.version.copyMaterial) {
          if (copy.thickness !== 4) {
            costs[copy.id] = {}
            costs[copy.id].materialTotalCost = copy?.qtyOverride
              ? copy.sheetOverride.cost * copy.qtyOverride
              : copy.sheetOverride.cost * copy.qty
            costs.material += costs[copy.id]?.materialTotalCost || 0
          }
        }
      }
      return costs
    },
    combinedCosts() {
      const urtils = this.version.pricing?.urtils || {}
      return {
        ...this.primaryCosts,
        cad: this.primaryCosts.cad + (urtils.programmingCost || 0),
        cutting: this.primaryCosts.cutting + (urtils.cuttingCost || 0),
        edgeFinishing: this.primaryCosts.edgeFinishing + (urtils.edgeFinishingCost || 0),
        faceFinishing: this.primaryCosts.faceFinishing + (urtils.surfaceFinishingCost || 0),
        glassDoorCost: urtils.glassDoorCost || 0,
        packaging: this.primaryCosts.packaging + (urtils.packaging || 0),
        packagingPerSqFt: this.primaryCosts.packagingPerSqFt + (urtils.packagingPerSqFt || 0),
        area: this.primaryCosts.area + (urtils.area || 0),
        perimeter: this.primaryCosts.perimeter + (urtils.perimeter || 0)
      }
    },
    total() {
      return (
        this.combinedCosts.material +
        this.combinedCosts.cad +
        this.combinedCosts.cutting +
        this.combinedCosts.packaging +
        this.combinedCosts.packagingPerSqFt +
        this.combinedCosts.edgeFinishing +
        this.combinedCosts.faceFinishing +
        this.combinedCosts.handlesCost +
        this.combinedCosts.glassDoorCost +
        (this.version.pricing?.shipping || 0) * 2 +
        (this.version.pricing?.worktops?.cost || 0)
      )
    },
    totalPrimaryCosts() {
      return (
        this.primaryCosts.cad +
        this.primaryCosts.cutting +
        this.primaryCosts.packaging +
        this.primaryCosts.packagingPerSqFt +
        this.primaryCosts.edgeFinishing +
        this.primaryCosts.faceFinishing +
        this.primaryCosts.handlesCost +
        (this.version.pricing?.shipping || 0) * 2 +
        (this.version.pricing?.worktops?.cost || 0)
      )
    },
    materialDataString() {
      let materialCombo = ''
      let materialComboArray = []

      for (const material of this.materials) {
        if (material.label.includes('Fenix')) {
          if (!materialComboArray.includes('Fenix')) {
            materialComboArray.push('Fenix')
          }
        } else if (material.label.includes('Formica')) {
          if (!materialComboArray.includes('Formica')) {
            materialComboArray.push('Formica')
          }
        } else if (material.label.includes('Birch')) {
          if (!materialComboArray.includes('Birch')) {
            materialComboArray.push('Birch')
          }
        } else if (material.label.includes('Oak')) {
          if (!materialComboArray.includes('Oak')) {
            materialComboArray.push('Oak')
          }
        } else if (material.label.includes('Walnut')) {
          if (!materialComboArray.includes('Walnut')) {
            materialComboArray.push('Walnut')
          }
        } else if (material.label.includes('Maple')) {
          if (!materialComboArray.includes('Maple')) {
            materialComboArray.push('Maple')
          }
        }
      }

      materialComboArray.forEach((material, index) => {
        if (index + 1 == materialComboArray.length) {
          materialCombo += material
        } else {
          materialCombo += material + ' / '
        }
      })

      return materialCombo
    },
    handleDataString() {
      let handleCombo = ''

      this.version.handles.forEach((handleType, index) => {
        if (index + 1 == this.version.handles.length) {
          handleCombo += handleType.value
        } else {
          handleCombo += handleType.value + ' / '
        }
      })

      return handleCombo
    }
  },
  methods: {
    async loadPricing() {
      this.loading = true

      const orderRef = await getDoc(doc(db, 'orders', this.orderID))
      this.order = orderRef.data()

      const quoteRef = await getDoc(doc(db, 'quotes', orderRef.data().quoteID))

      this.version = this.$utils.getVersionByID(quoteRef.data().versions, orderRef.data().versionID)

      await MaterialResolver.prefetchMaterialsForVersion(this.version)

      const pricing = usPricing.fronts(
        this.version.fronts.concat(this.version.coverPanels),
        this.version.materials,
        this.version.handles
      )

      // Get urtils pricing to combine material usage
      let urtilsPricing = null
      try {
        if (this.version.urtils && this.version.urtils.length > 0) {
          urtilsPricing = usPricing.urtils(this.version.urtils, this.version.materials, 1)
        }
      } catch (e) {
        console.error('Error calculating urtils pricing:', e)
      }

      for (const m of this.version.materials) {
        let material = pricing[m.id]

        // Check if this material is used by urtils (even if not by fronts)
        const hasUrtilUsage = urtilsPricing && urtilsPricing[m.id] && urtilsPricing[m.id].areaWithWastage > 0

        // If fronts doesn't use this material but urtils does, create a new entry
        if (!material && hasUrtilUsage) {
          const urtilMaterial = urtilsPricing[m.id]
          const sheetsNeeded = Math.ceil(urtilMaterial.areaWithWastage / (m.w * m.h))

          // Use MasterPricing to get proper cost calculation (same as fronts)
          const materialPrice = MasterPricing.calculateUsPriceForMaterial(
            m,
            urtilMaterial.area,
            urtilMaterial.perimeter,
            sheetsNeeded
          )

          const sheetCost = materialPrice ? materialPrice.details.materialCost / sheetsNeeded : 0

          material = {
            id: m.id,
            label: m.label,
            value: m.value,
            name: m.colourCode || m.value,
            thickness: m.thickness,
            area: urtilMaterial.area,
            areaWithWastage: urtilMaterial.areaWithWastage,
            perimeter: urtilMaterial.perimeter,
            itemCount: urtilMaterial.itemCount,
            sheetArea: m.w * m.h,
            requiredSheetsRounded: sheetsNeeded,
            sheetCost: sheetCost,
            total: materialPrice ? materialPrice.total : 0,
            // Set other costs to 0 since urtils tracks them separately
            programmingCost: 0,
            cuttingCost: 0,
            edgeFinishingCost: 0,
            surfaceFinishingCost: 0,
            packagingPerSqFt: 0,
            handlesCost: 0,
            lucidHandlesCost: 0
          }
        } else if (material) {
          material.id = m.id
          material.label = m.label
          material.value = m.value

          // Add urtils material usage to existing fronts material
          if (hasUrtilUsage) {
            const urtilMaterial = urtilsPricing[m.id]
            const urtilSheetsNeeded = Math.ceil(urtilMaterial.areaWithWastage / (m.w * m.h))
            material.requiredSheetsRounded += urtilSheetsNeeded
            material.areaWithWastage += urtilMaterial.areaWithWastage
            material.itemCount += urtilMaterial.itemCount

            // If fronts didn't set a sheetCost (e.g., 6mm material only used by urtils), calculate it
            if (!material.sheetCost || material.sheetCost === 0) {
              const materialPrice = MasterPricing.calculateUsPriceForMaterial(
                m,
                urtilMaterial.area,
                urtilMaterial.perimeter,
                urtilSheetsNeeded
              )
              if (materialPrice) {
                material.sheetCost = materialPrice.details.materialCost / urtilSheetsNeeded
                material.total = materialPrice.total
              }
            }
          }
        }

        if (material) {
          this.materials.push(material)
        }
      }

      this.loading = false
    },
    csv() {
      let s =
        'Plykea Order Number,Customer Email,Customer Total Payment,Material Type,Handle Type,Total Sheets,Total Area (sq ft),Total Linear (ft),JHandle Linear (ft),Insert Count,Total Material Cost,CAD,CNC,Edge Finishing,Face Finishing,Handles,Packaging (fixed),Packaging per Sq Ft,Worktops,Freight,Lucid Total\n'
      let orderData = `${this.order.orderNumber},${this.order.customerEmail},${utils.toFixedNumber(
        this.customerPaidTotal,
        2
      )}`
      let materialData = this.materialDataString
      let handleData = this.handleDataString
      let sheetNumData = this.totalSheetCount
      let breakdownData = `${utils.toFixedNumber(this.combinedCosts.area, 2)},${utils.toFixedNumber(
        this.combinedCosts.perimeter,
        2
      )},${utils.toFixedNumber(this.combinedCosts.jHandlesPerimeter, 2)},${this.combinedCosts.numHandles}`

      let pricingData = `${utils.toFixedNumber(this.combinedCosts.material, 2)},${utils.toFixedNumber(
        this.combinedCosts.cad,
        2
      )},${utils.toFixedNumber(this.combinedCosts.cutting, 2)},${utils.toFixedNumber(
        this.combinedCosts.edgeFinishing,
        2
      )},${utils.toFixedNumber(this.combinedCosts.faceFinishing, 2)},${utils.toFixedNumber(
        this.combinedCosts.handlesCost,
        2
      )},${utils.toFixedNumber(this.combinedCosts.packaging, 2)},${utils.toFixedNumber(
        this.combinedCosts.packagingPerSqFt,
        2
      )},${utils.toFixedNumber(this.version.pricing?.worktops?.cost || 0, 2)},${utils.toFixedNumber(
        (this.version.pricing?.shipping || 0) * 2,
        2
      )}`
      let lucidTotalData = `${utils.toFixedNumber(this.total, 2)}`
      if (this.version.lucidCoverCost) lucidTotalData += ' (lucid fault)'

      s +=
        orderData +
        ',' +
        materialData +
        ',' +
        handleData +
        ',' +
        sheetNumData +
        ',' +
        breakdownData +
        ',' +
        pricingData +
        ',' +
        lucidTotalData +
        '\n'

      var blob = new Blob([s], { type: 'text/csv' })
      filesaver.saveAs(blob, `usPricing${-this.order.orderNumber}.csv`)
    }
  }
}
</script>
